import React, { useCallback } from 'react';
import TextInputEditor from './TextInputEditor';
import {
  FILLABLE_TEXT_INPUT_TYPE,
  FILLABLE_SIGNATURE_TYPE,
  OPTIONS_KEY,
  INPUT_TEXT_ELEMENT_OPTIONS,
  FILLABLE_COMBO_BOX_TYPE,
  FILLABLE_RADIO_BUTTON_GROUP_TYPE,
  FILLABLE_CHECKBOX_GROUP_TYPE,
  FILLABLE_CHECKBOX_TYPE,
  FILLABLE_RADIO_BUTTON_TYPE,
  FILLABLE_COMBOBOX_GROUP_TYPE,
} from '../../../utils/complexDocumentConstants';
import SignatureEditor from './SignatureEditor';
import ComboBoxEditor from './ComboBoxEditor';
import ReusableCheckbox from './ReusableCheckbox';
import ReusableRadioButton from './ReusableRadioButton';
import { CheckboxGroup } from '../GridDesign/DynamicFormElements/CheckboxGroup';
import { RadiobuttonGroup } from '../GridDesign/DynamicFormElements/RadiobuttonGroup';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';

const FillableElementRenderer = ({
  element,
  editable,
  selected,
  scaling,
  customClassName = 'selectable',
  ...props
}) => {
  const {
    staticTableBlock,
    refreshSelectedElementIds,
    parentStoredElements,
    updateParentComplexElementDetails,
    parentUUID,
    onCellDoubleClick,
    updateElementRect,
    elementIndex
  } = props;

  if (props?.highlightId && props?.highlightId === element.groupId) {
    customClassName += " highlight";
  }

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN] || store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `FillableElementRenderer`);

  const elementStyle = Object.assign({}, element.style, {
    transform:
      `translateX(${element.style.x}px) translateY(${element.style.y}px)` +
      ` rotate(${element.style.rotate}deg)`,
    border: element.style.border
      ? `${element.style.border?.thickness ? `${element.style.border.thickness}px` : '1px'} solid ${
          element.style.border.borderColor
        }`
      : 'none',
    position: staticTableBlock ? 'static' : 'absolute',
    width: `${element.style.width}${staticTableBlock ? '%' : 'px'}`,
    height: `${element.style.height}px`,
  });
  delete elementStyle.x;
  delete elementStyle.y;
  const containerProps = {
    "data-group-uuid": element.groupId,
    "data-uuid": element.uuid,
    className: customClassName,
    "data-element-type": element.fillableType,
    "data-element-index": elementIndex
  };
  if (element.fillableType === FILLABLE_TEXT_INPUT_TYPE) {
    if (element?.[OPTIONS_KEY]?.[INPUT_TEXT_ELEMENT_OPTIONS.MULTILINE_KEY]) {
      elementStyle.overflowY = 'auto';
      elementStyle.overflowX = 'hidden';
    } else {
      elementStyle.overflowY = 'hidden';
      elementStyle.overflowX = 'auto';
    }
    return (
      <div
        style={elementStyle}
        data-inputbox-enablescroll={element[OPTIONS_KEY][INPUT_TEXT_ELEMENT_OPTIONS.MULTILINE_KEY]}
        {...containerProps}
      >
        <TextInputEditor
          element={element}
          selected={selected}
          editable={editable}
          scaling={scaling}
          staticTableBlock={staticTableBlock}
        />
      </div>
    );
  } else if (element.fillableType === FILLABLE_SIGNATURE_TYPE) {
    return (
      <div
        style={elementStyle}
        {...containerProps}
      >
        <SignatureEditor element={element} selected={selected} editable={editable} />
      </div>
    );
  } else if (element.fillableType === FILLABLE_COMBO_BOX_TYPE) {
    return (
      <div
        style={elementStyle}
        {...containerProps}
      >
        <ComboBoxEditor
          element={element}
          selected={selected}
          editable={editable}
          staticTableBlock={staticTableBlock}
          isInteractiveDesign={isInteractiveDesign}
        />
      </div>
    );
  } else if (element.fillableType === FILLABLE_CHECKBOX_GROUP_TYPE) {
    elementStyle.height = `auto`;
    const { color, backgroundColor, ...elStyles } = elementStyle;
    return (
      <div
        style={elStyles}
        {...containerProps}
      >
        <CheckboxGroup
          element={element}
          editable={editable}
          selected={selected}
          updateElementRect={updateElementRect}
        />
      </div>
    );
  } else if (element.fillableType === FILLABLE_RADIO_BUTTON_GROUP_TYPE) {
    elementStyle.height = `auto`;
    const { color, backgroundColor, ...elStyles } = elementStyle;
    return (
      <div
        style={elStyles}
        {...containerProps}
      >
        <RadiobuttonGroup
          element={element}
          editable={editable}
          selected={selected}
          updateElementRect={updateElementRect}
        />
      </div>
    );
  } else if (element.fillableType === FILLABLE_COMBOBOX_GROUP_TYPE) {
    return (
      <div
        style={elementStyle}
        {...containerProps}
      >
        <ComboBoxEditor
          element={element}
          editable={editable}
          selected={selected}
          staticTableBlock={staticTableBlock}
          isComboboxGroup={true}
        />
      </div>
    );
  } else if (element.fillableType === FILLABLE_CHECKBOX_TYPE) {
    elementStyle.width = `${element.style.width}px`;
    return (
      <div
        style={elementStyle}
        {...containerProps}
      >
        <ReusableCheckbox
          element={element}
          editable={editable}
          selected={selected}
          staticTableBlock={staticTableBlock}
          refreshSelectedElementIds={refreshSelectedElementIds}
          parentUUID={parentUUID}
          parentStoredElements={parentStoredElements}
          updateParentComplexElementDetails={updateParentComplexElementDetails}
          onCellDoubleClick={onCellDoubleClick}
          isInteractiveDesign={isInteractiveDesign}
        />
      </div>
    );
  } else if (element.fillableType === FILLABLE_RADIO_BUTTON_TYPE) {
    const { border, backgroundColor, color, ...newElementStyle } = elementStyle;
    newElementStyle.width = `${element.style.width}px`;
    return (
      <div
        style={newElementStyle}
        {...containerProps}
      >
        <ReusableRadioButton
          element={element}
          editable={editable}
          selected={selected}
          staticTableBlock={staticTableBlock}
          refreshSelectedElementIds={refreshSelectedElementIds}
          parentUUID={parentUUID}
          parentStoredElements={parentStoredElements}
          updateParentComplexElementDetails={updateParentComplexElementDetails}
          onCellDoubleClick={onCellDoubleClick}
          isInteractiveDesign={isInteractiveDesign}
        />
      </div>
    );
  }
  return <div />;
};

export default React.memo(FillableElementRenderer);
